import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2Icon, PenLineIcon, StarIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useReviews } from '../../hooks/useReviews';
import { reviewsAggregate } from '../../data/reviews';
import { testimonials } from '../../data/testimonials';
import type { Testimonial } from '../../data/testimonials';
import { buttonClasses } from '../../utils/buttonStyles';
import { RatingStars } from '../reviews/RatingStars';
import { ReviewForm } from '../reviews/ReviewForm';
import { REVIEWS_SECTION_ID } from '../reviews/ReviewSummaryBar';

/** Customer Reviews header (rating + write a review) above two endless rows of testimonial cards. */
export function TestimonialsMarquee() {
  const {
    lang
  } = useLanguage();
  const es = lang === 'es';
  const {
    summary,
    hasReviewed,
    submit
  } = useReviews();
  const [writing, setWriting] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const fmt = (n: number) => n.toLocaleString(es ? 'es-US' : 'en-US');
  const showRows = testimonials.length >= 4;
  const half = Math.ceil(testimonials.length / 2);
  const rows = [testimonials.slice(0, half), testimonials.slice(half)];
  return <section id={REVIEWS_SECTION_ID} tabIndex={-1} aria-labelledby="reviews-title" className="mf-tech-grid scroll-mt-[72px] overflow-hidden border-t mf-hairline bg-deep py-10 focus:outline-none md:py-14" data-id="element-404" data-mp="26ijatnriy4uq">
      <div className="mx-auto max-w-site px-5 md:px-8" data-id="element-405" data-mp="1wlewub7u1fp0">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between" data-id="element-406" data-mp="1it6zoicmzmps">
          <div data-id="element-407" data-mp="1nsbatej4fuir">
            <h2 id="reviews-title" className="font-display text-5xl font-semibold uppercase leading-[0.92] md:text-7xl" data-id="element-408" data-mp="1ns1smd4birnm">
              {es ? 'Reseñas de clientes' : 'Customer Reviews'}
            </h2>
            <div className="mt-8 flex items-end gap-4" data-id="element-409" data-mp="1dunewl9s4gz0">
              <span className="font-display text-7xl font-semibold leading-none text-white" data-id="element-410" data-mp="1dtcfexnd9vdp">
                {summary.count ? summary.average.toFixed(1) : '—'}
              </span>
              <div className="pb-1" data-id="element-411" data-mp="px7mnyrtdlrv">
                <RatingStars value={summary.average} size={22} decorative data-id="element-412" data-mp="kzpalzii2qfn" />
                <p className="mt-1 text-sm text-titanium" data-id="element-413" data-mp="kzz1rqfa73mx">
                  {summary.count ? summary.isAggregate && reviewsAggregate ? `${fmt(summary.count)}+ ${es ? 'reseñas' : 'reviews'} · ${es ? 'desde' : 'since'} ${reviewsAggregate.sinceYear}` : `${fmt(summary.count)} ${es ? 'reseñas verificadas' : 'verified reviews'}` : es ? 'Aún no hay reseñas publicadas' : 'No published reviews yet'}
                </p>
              </div>
            </div>
          </div>

          <div className="w-full lg:w-[320px]" data-id="element-414" data-mp="3ohpmhpr6ffyj">
            {hasReviewed ? <div className="flex items-start gap-3 rounded-xl border border-marine/40 bg-marine/[0.08] p-4" role="status" data-id="element-415" data-mp="3jgju5t1fhupj">
                <CheckCircle2Icon className="mt-0.5 h-5 w-5 shrink-0 text-marine" aria-hidden data-id="element-416" data-mp="1yx7mat5zvjnn" />
                <p className="font-semibold text-white" data-id="element-417" data-mp="23ynauv7ds8as">
                  {justSubmitted ? es ? '¡Gracias! Recibimos tu reseña.' : 'Thank you! We received your review.' : es ? 'Ya dejaste tu reseña. ¡Gracias!' : 'You already left a review. Thank you!'}
                </p>
              </div> : !writing && <button type="button" onClick={() => setWriting(true)} className={`${buttonClasses('primary')} w-full`} data-id="element-418" data-mp="2rozlidu0gj52">
                  <PenLineIcon className="h-4 w-4" aria-hidden data-id="element-419" data-mp="2wm9n0ruocl99" /> {es ? 'Escribir una reseña' : 'Write a review'}
                </button>}
          </div>
        </div>

        <AnimatePresence initial={false} data-id="element-420" data-mp="129jqe2cx8xy6">
          {writing && !hasReviewed && <motion.div initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} className="mt-10 max-w-2xl" data-id="element-421" data-mp="2sou52l6mpw6t">
              <ReviewForm onCancel={() => setWriting(false)} onSubmit={r => {
            submit(r);
            setWriting(false);
            setJustSubmitted(true);
          }} data-id="element-422" data-mp="2xoyn4fn6qyz5" />
            </motion.div>}
        </AnimatePresence>

        {showRows && <p className="mt-8 text-sm font-semibold uppercase tracking-[0.14em] text-titanium/80" data-id="element-423" data-mp="2it8w23357vnk">
            {es ? 'Primeras impresiones del diseño del M1 PRO' : 'First impressions of the M1 PRO design'}
          </p>}
      </div>

      {showRows && <div className="mf-marquee-wrap mt-5 space-y-5" data-id="element-424" data-mp="1mautxbof9ev5">
          {rows.map((row, r) => <div key={r} className="mf-marquee-mask" data-id="element-425" data-mp="1c5uwj2p7fbdu" data-mpi={r}>
              <ul className={`mf-marquee flex w-max gap-5 ${r === 1 ? 'mf-marquee-rev' : ''}`} data-id="element-426" data-mp="1c41phmaoip5b">
                {[...row, ...row].map((t, i) => <TestimonialCard key={`${t.name}-${i}`} t={t} hidden={i >= row.length} data-id="element-427" data-mp="128q9z1rg5dwi" data-mpi={i} />)}
              </ul>
            </div>)}
        </div>}
    </section>;
}
function TestimonialCard({
  t,
  hidden
}: {
  t: Testimonial;
  hidden: boolean;
}) {
  return <li aria-hidden={hidden || undefined} className="flex w-[300px] shrink-0 flex-col border border-marine/20 bg-[#06121F]/90 p-6 sm:w-[360px]" data-id="element-428" data-mp="1zw8pqwjizga0">
      <div className="flex gap-0.5" aria-label={`${t.rating} out of 5 stars`} role="img" data-id="element-429" data-mp="1l0j4x1q2t96v">
        {[0, 1, 2, 3, 4].map(i => <StarIcon key={i} className={`h-4 w-4 ${i < t.rating ? 'fill-[#FFC94D] text-[#FFC94D]' : 'text-white/20'}`} aria-hidden data-id="element-430" data-mp="9irqrmzxoazr" />)}
      </div>
      <p className="mt-4 font-display text-xl font-semibold uppercase leading-tight tracking-[0.03em] text-white" data-id="element-431" data-mp="2h5jdgpul1j0s">{t.title}</p>
      <p className="mt-2 text-[15px] leading-relaxed text-titanium" data-id="element-432" data-mp="2c6ztuw3gojbl">{t.body}</p>
      <p className="mt-auto pt-5 text-sm font-semibold text-ice" data-id="element-433" data-mp="27egd5q9h982k">{t.name}</p>
      {t.detail && <p className="mt-0.5 text-xs text-titanium/70" data-id="element-434" data-mp="22gn8x2rqio4x">{t.detail}</p>}
    </li>;
}